import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Instagram, Facebook, Youtube, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const TwitterIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

interface FooterProps {
  onDownloadClick: () => void;
  onNavigateToRole?: (role: string) => void;
  onComingSoonClick?: () => void;
}

const Footer: React.FC<FooterProps> = ({ onDownloadClick, onNavigateToRole, onComingSoonClick }) => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const termsHref = isEn ? '/en/terms_and_conditions' : '/terminos_y_condiciones';
  const privacyHref = isEn ? '/en/privacy-policy' : '/politicas-de-privacidad';
  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/mas.trainer.app', label: 'Instagram' },
    { icon: Facebook, href: 'https://www.facebook.com/people/M%C3%A1s-Trainer-App/61552425156573/', label: 'Facebook' },
    { icon: TwitterIcon, href: 'https://twitter.com/mas_trainer_app', label: 'Twitter' },
    { icon: Youtube, href: 'https://www.youtube.com/channel/UCkqRHv7C1d8z_Q0mpaHHRZw', label: 'YouTube' },
  ];

  const serviciosApp = [
    { label: 'Planificación de entrenamientos', role: 'seguidor' as const },
    { label: 'Monitoreo de carga de trabajo', role: 'jugador' as const },
    { label: 'Prevención de lesiones', role: 'gestiono-equipo' as const },
  ];

  const miembrosLinks = [
    { label: 'Entrenadores', href: '#entrenadores' },
    { label: 'Instituciones', href: '#instituciones' },
    { label: 'Licencias', href: '#licencias' },
  ];

  const handleServicioClick = (role: string) => {
    onNavigateToRole?.(role);
    document.getElementById('entrenadores')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-gradient-to-r from-sky-50 via-white to-amber-50/80 text-gray-800 border-t border-gray-200">
      <div className="container-custom px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- Logo links to app root (/) */}
              <a href="/" className="inline-block">
                <span className="text-2xl md:text-3xl font-bold text-mas10-primary font-display">+Trainer</span>
              </a>
              <div className="w-12 h-0.5 bg-amber-400 rounded mt-2 mb-4" aria-hidden />
              <p className="text-gray-600 text-sm mb-5">Desatá tu Potencial</p>
              <div className="flex gap-2">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-mas10-primary text-white flex items-center justify-center hover:bg-mas10-secondary transition-colors"
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
              <motion.button
                type="button"
                onClick={onDownloadClick}
                className="mt-5 flex items-center gap-2 bg-mas10-primary hover:bg-blue-800 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Download className="w-4 h-4" />
                Descargar App
              </motion.button>
            </motion.div>
          </div>

          {/* Servicios de la aplicación */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              viewport={{ once: true }}
            >
              <h3 className="text-sm font-bold text-mas10-primary uppercase tracking-wide mb-2">Servicios de la aplicación</h3>
              <div className="w-10 h-0.5 bg-amber-400 rounded mb-4" aria-hidden />
              <ul className="space-y-2">
                {serviciosApp.map((item, index) => (
                  <li key={index}>
                    <button
                      type="button"
                      onClick={() => handleServicioClick(item.role)}
                      className="text-gray-600 hover:text-mas10-primary text-left text-sm transition-colors"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Miembros */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-sm font-bold text-mas10-primary uppercase tracking-wide mb-2">Miembros</h3>
              <div className="w-10 h-0.5 bg-amber-400 rounded mb-4" aria-hidden />
              <ul className="space-y-2">
                {miembrosLinks.map((link, index) => (
                  <li key={index}>
                    {'comingSoon' in link && link.comingSoon ? (
                      <button
                        type="button"
                        onClick={onComingSoonClick}
                        className="text-gray-600 hover:text-mas10-primary text-sm transition-colors text-left"
                      >
                        {link.label}
                      </button>
                    ) : (
                      <a
                        href={link.href}
                        className="text-gray-600 hover:text-mas10-primary text-sm transition-colors"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Contactanos */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              viewport={{ once: true }}
            >
              <h3 className="text-sm font-bold text-mas10-primary uppercase tracking-wide mb-2">Contactanos</h3>
              <div className="w-10 h-0.5 bg-amber-400 rounded mb-4" aria-hidden />
              <div className="space-y-3 text-gray-600 text-sm">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-mas10-primary flex-shrink-0" />
                  <a href="tel:+5493512392367" className="hover:text-mas10-primary transition-colors">+5493512392367</a>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-mas10-primary flex-shrink-0" />
                  <span>Argentina</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-mas10-primary flex-shrink-0" />
                  <a href="mailto:hola@mastrainerapp.com" className="hover:text-mas10-primary transition-colors">hola@mastrainerapp.com</a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="border-t border-gray-200 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-500"
        >
          <p>© {new Date().getFullYear()} +Trainer. {t('footer.rights')}</p>
          <div className="flex items-center gap-4">
            <Link to={termsHref} className="hover:text-mas10-primary transition-colors">
              {t('footer.terms')}
            </Link>
            <span aria-hidden>·</span>
            <Link to={privacyHref} className="hover:text-mas10-primary transition-colors">
              {t('footer.privacy')}
            </Link>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
